import React from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { Textarea } from '@/components/form';
import { Button, LoaderScreen, Typography } from '@/components/common';
import Icons from '@/const/icons';
import {
	ConversationHeader,
	ConversationPagination,
	ConversationThread,
	GenericCard,
} from '@/components/dashboard';
import { inboxService } from '@/api/inbox';
import {
	Conversation,
	ConversationList,
	NewAddMessagePayload,
} from '@/interfaces/inbox';
import { useRouter } from 'next/router';
import { useNotification } from '@/hooks';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import {
	GET_CLIENT_ACCOUNT_DETAILS,
	GET_CONVERSATION_INFO,
	GET_CONVERSATIONS,
} from '@/api';
import AppRoutes from '@/const/routes';
import { useEventSource } from '@/hooks/useEventSource';
import { useSession } from 'next-auth/react';
import { formatErrorMessage } from '@/lib';
import { ModalResolved } from '../../dashboard/inbox/ModalResolved';
import { END_STATUSES_CASE } from '@/const/cases';
import FlagClient from '../client-profile/account-details/FlagClient';
import { ReviewBanner } from '../client-profile/account-details/ReviewBanner';
import { clientProfileService } from '@/api/clientProfile';
import clsx from 'clsx';

export const ConversationLawyerScreen: React.FC = () => {
	const { toastError, toastSuccess } = useNotification();
	const session = useSession();
	const token = session.data?.accessToken;
	const [showReviewBanner, setShowReviewBanner] =
		React.useState<boolean>(false);
	const { data: eventResponse } = useEventSource({
		url: `${process.env.NEXT_PUBLIC_API}/sse/new-message/subscribe?token=${token}`,
	});
	const [isLoadingSendMessage, setIsLoadingSendMessage] =
		React.useState<boolean>(false);
	const [isLoading, setIsLoading] = React.useState(true);

	//router
	const router = useRouter();
	// id is the actual conversation ID from the path
	// page and limit are from the InboxList pagination context
	// fids is JSON string of filtered conversation IDs from InboxList
	// cidx is the string index of the current conversation within fids
	const { id, page, limit, fids, cidx } = router.query;

	const filteredIds: string[] = React.useMemo(() => {
		try {
			return fids ? JSON.parse(fids as string) : [];
		} catch (e) {
			console.error('Failed to parse fids:', e);
			return [];
		}
	}, [fids]);

	const currentIndexInFiltered: number = React.useMemo(() => {
		const parsedCidx = parseInt(cidx as string, 10);
		return !isNaN(parsedCidx) ? parsedCidx : -1;
	}, [cidx]);

	// pagination
	const containerRef = React.useRef<any>(null);
	const prevScrollPos = React.useRef(0);
	const [conversation, setConversation] = React.useState<Conversation[]>([]);

	// form
	const {
		register,
		formState: { errors, isDirty, isValid },
		handleSubmit,
		reset,
	} = useForm();

	const rules = {
		message: {
			required: { value: true, message: 'This is required.' },
		},
	};

	const fetchData = async ({
		pageParam = 1,
	}: {
		pageParam?: number;
	}): Promise<ConversationList> => {
		const response = await inboxService.getConversations({
			conversationId: (id as string) || '',
			page: pageParam,
			limit: 10,
		});
		return response.data;
	};

	const {
		data: conversationPaginated,
		fetchNextPage,
		refetch,
		hasNextPage,
		isFetchingNextPage,
		isError,
	} = useInfiniteQuery({
		initialData: undefined,
		initialPageParam: 1,
		queryKey: [GET_CONVERSATIONS, id],
		queryFn: fetchData,
		getNextPageParam(lastPage) {
			return lastPage.page < lastPage.totalPages
				? lastPage.page + 1
				: undefined;
		},
		enabled: !!id,
	});

	const { data: conversationInfo, refetch: refetchConversationInfo } = useQuery(
		{
			queryKey: [GET_CONVERSATION_INFO, id],
			queryFn: () => inboxService.getConversationInfo((id as string) || ''),
			enabled: !!id,
			refetchOnWindowFocus: false,
		}
	);

	const { data: accountDetails, refetch: refetchAccountDetails } = useQuery({
		queryKey: [GET_CLIENT_ACCOUNT_DETAILS, conversationInfo],
		queryFn: () =>
			clientProfileService.getAccountDetails(
				conversationInfo?.data.to.id as string
			),
		enabled: !!conversationInfo?.data.to.id,
		refetchOnWindowFocus: false,
	});
	const isActive = accountDetails?.data.status === 'Active';
	// ----------- Use effects ----------------//
	React.useEffect(() => {
		const container = containerRef.current;
		const scrollToBottom = () => {
			if (container) {
				container.scrollTop = container.scrollHeight;
			}
		};
		// Call function to scroll to end only on first load
		if (
			conversationPaginated &&
			conversationPaginated.pageParams.length === 1
		) {
			setTimeout(() => {
				scrollToBottom();
			}, 10);
		} else if (conversationPaginated) {
			containerRef.current.scrollTop = prevScrollPos.current;
		}

		const handleScroll = () => {
			const { scrollTop, clientHeight } = container;
			let scrollCurrent = clientHeight;

			if (conversationPaginated) {
				scrollCurrent = clientHeight * conversationPaginated.pageParams.length;
			}

			if (scrollTop === 0 && hasNextPage) {
				prevScrollPos.current = scrollCurrent;
				fetchNextPage();
			}
		};

		container?.addEventListener('scroll', handleScroll);

		return () => {
			container?.removeEventListener('scroll', handleScroll);
		};
	}, [conversationPaginated, containerRef, fetchNextPage, hasNextPage]);

	React.useEffect(() => {
		const container = containerRef.current;

		const scrollToBottom = () => {
			if (container) {
				container.scrollTop = container.scrollHeight;
			}
		};

		if (eventResponse) {
			refetch();
			scrollToBottom();
		}
	}, [eventResponse, refetch]);

	React.useEffect(() => {
		const revertConversation =
			conversationPaginated?.pages
				.reverse()
				.flatMap((pageItem) => pageItem.items) || [];
		setConversation(revertConversation);
	}, [conversationPaginated]);
	React.useEffect(() => {
		if (accountDetails) {
			setShowReviewBanner(
				accountDetails.data.isFlagged?.status === 'Review Required'
			);

			setIsLoading(false);
		}
	}, [accountDetails]);
	// ------------- Functions -------------------//

	const handleAddMessage = async (data: FieldValues): Promise<void> => {
		setIsLoadingSendMessage(true);
		try {
			const newData: NewAddMessagePayload = {
				conversationId: id as string,
				content: data.message,
				is_bot: false,
			};
			await inboxService.postAddMessage(newData);
			reset();
			refetch();
			toastSuccess('Your message was sent successfully.');
		} catch (error) {
			toastError(formatErrorMessage((error as any).message));
		} finally {
			setIsLoadingSendMessage(false);
		}
	};

	const navigateToConversation = (newIndex: number) => {
		if (
			filteredIds.length > 0 &&
			newIndex >= 0 &&
			newIndex < filteredIds.length
		) {
			const nextConversationId = filteredIds[newIndex];
			router.push({
				pathname: `${AppRoutes.LAWYERAPP_INBOX}/${nextConversationId}`,
				query: {
					page: page, // Persist original page from InboxList
					limit: limit, // Persist original limit from InboxList
					conversationId: nextConversationId,
					fids: JSON.stringify(filteredIds),
					cidx: newIndex.toString(),
				},
			});
		} else {
			console.warn(
				'Cannot navigate, newIndex is out of bounds or filteredIds is empty.'
			);
		}
	};

	const handleNextConversation = (): void => {
		if (
			currentIndexInFiltered >= 0 &&
			currentIndexInFiltered < filteredIds.length - 1
		) {
			navigateToConversation(currentIndexInFiltered + 1);
		} else {
			console.warn('Already at the last conversation in the filtered list.');
			// Optionally, you could toast a message to the user
		}
	};

	const handlePrevConversation = (): void => {
		if (currentIndexInFiltered > 0) {
			navigateToConversation(currentIndexInFiltered - 1);
		} else {
			console.warn('Already at the first conversation in the filtered list.');
			// Optionally, you could toast a message to the user
		}
	};

	const handleResolvedConversation = async (callback: any) => {
		try {
			await inboxService.resolvedConversation(conversationInfo?.data.id || '');
			callback();
			toastSuccess('This message has been successfully closed', 'bottom-right');
			refetch();
			refetchConversationInfo();
		} catch (error) {
			toastError('An error occurred. Please try again.');
			console.log('ERR', error);
		}
	};

	// if (isLoading) return <LoaderScreen />;
	return (
		<GenericCard className="!h-full">
			{showReviewBanner && isActive && <ReviewBanner />}
			<div
				className={clsx(
					'flex flex-col bg-grey-2 px-8 py-6 rounded-2xl overflow-y-auto scroll-custom',
					{
						'h-[calc(100%-1rem)]': conversationInfo?.data.status === 'OPEN',
						'!h-[calc(100%-4rem)]': showReviewBanner,
						'h-full':
							conversationInfo?.data.status !== 'OPEN' && !showReviewBanner,
					}
				)}
			>
				{isLoading ? (
					<LoaderScreen />
				) : (
					<>
						<ConversationPagination
							index={`${
								currentIndexInFiltered >= 0 ? currentIndexInFiltered + 1 : ''
							}`}
							totalPages={filteredIds.length}
							prevPath={AppRoutes.LAWYERAPP_INBOX}
							handleNextConversation={handleNextConversation}
							handlePrevConversation={handlePrevConversation}
						/>
						<ConversationHeader
							caseTitle={conversationInfo?.data.case.title || ''}
							category={conversationInfo?.data.case.category.name}
							subject={conversationInfo?.data.subject.title || ''}
							to={conversationInfo?.data.to}
							from={conversationInfo?.data.from}
							date={conversationInfo?.data.createdAt || ''}
							isYourLawyer={conversationInfo?.data.isYourLawyer}
						/>
						{isError ? (
							<div className="flex justify-center items-center w-full h-full">
								<Typography type="semibold-14" className="my-10">
									Unexpected Error
								</Typography>
							</div>
						) : (
							<>
								{isFetchingNextPage && <LoaderScreen />}
								<ConversationThread
									conversations={conversation ? conversation : []}
									containerRef={containerRef}
									clientTo={conversationInfo?.data.to}
								/>
								{conversationInfo ? (
									conversationInfo?.data.status === 'OPEN' ? (
										<form
											className="bg-white border border-[#0D061614] rounded-[32px] px-6 3xl:px-8 pb-6 mt-6"
											onSubmit={handleSubmit(handleAddMessage)}
										>
											<Textarea
												name="message"
												register={register}
												placeholder="Write your message here"
												rules={rules.message}
												error={errors.message}
												classNameContainerInput="border-none !rounded-none group-hover:!bg-transparent"
												classNameInput="border-none !rounded-none !bg-transparent !px-0 max-h-[50px] xl:max-h-[60px] 3xl:max-h-[100px]"
												disabled={
													END_STATUSES_CASE.includes(
														conversationInfo.data.case.progress_name
													) ||
													END_STATUSES_CASE.includes(
														conversationInfo.data.progress_applicant
													)
												}
											/>
											<div className="flex flex-col md:flex-row gap-2 items-start md:items-center justify-between">
												<Button
													decoration="primary"
													label="Send"
													size={'medium'}
													icon={Icons.sendEmail}
													iconRight
													disabled={
														!isValid || !isDirty || isLoadingSendMessage
													}
													className="!py-4 w-full md:w-auto"
													type="submit"
												/>
												<div className="flex flex-col md:flex-row items-start md:items-center gap-4">
													{!showReviewBanner && (
														<FlagClient
															clientId={conversationInfo.data.to.id}
															align="end"
															side="top"
															refetchData={refetchAccountDetails}
														/>
													)}
													<ModalResolved
														onClickContinue={handleResolvedConversation}
													/>
												</div>
											</div>
										</form>
									) : (
										<Typography
											type="regular-14"
											className="text-opacity-black text-center"
										>
											This conversation has been resolved and closed.
										</Typography>
									)
								) : null}
							</>
						)}
					</>
				)}
			</div>
		</GenericCard>
	);
};
